import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    signal,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BusinessServices, Country, ITeam, IUser, Roles } from '@shs-digi-care-monorepo/interfaces';
import {
    addDays,
    addMonths,
    addWeeks,
    differenceInDays,
    endOfDay,
    endOfMonth,
    isWeekend,
    startOfDay,
    startOfMonth,
    startOfWeek,
    subMonths,
    subWeeks
} from 'date-fns';
import { catchError, EMPTY, filter, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { PageLayoutComponent } from '../components';
import { Paginated } from '../models/paginated.model';
import { UserService } from '../services/user.service';
import { TeamService } from '../services/team.service';
import { administeredCountries, administersStream, roleNamesForStream } from '../util';
import { SnackbarService } from '../ui/sh-snackbar/snackbar.service';
import { ShCountryComponent } from '../ui/sh-country/sh-country.component';
import { ConfirmDialogComponent } from '../ui/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogData } from '../ui/confirm-dialog/confirm-dialog.model';
import { OutOfOfficeOverviewGridComponent } from './components/out-of-office-overview-grid/out-of-office-overview-grid.component';
import { OutOfOfficeEntryDetailComponent } from './components/out-of-office-entry-detail/out-of-office-entry-detail.component';
import { OutOfOfficeOverviewService } from './services/out-of-office-overview.service';
import {
    CalendarView,
    OutOfOfficeOverviewDelegation,
    OutOfOfficeOverviewEntry,
    TeamGroup,
    UserEvent
} from './models/out-of-office-overview.model';

@Component({
    selector: 'app-out-of-office-overview',
    standalone: true,
    templateUrl: './out-of-office-overview.component.html',
    imports: [
        PageLayoutComponent,
        TranslatePipe,
        OutOfOfficeOverviewGridComponent,
        OutOfOfficeEntryDetailComponent,
        ShCountryComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OutOfOfficeOverviewComponent implements OnInit {
    private readonly elementRef = inject(ElementRef);
    private readonly outOfOfficeOverviewService = inject(OutOfOfficeOverviewService);
    private readonly teamService = inject(TeamService);
    private readonly userService = inject(UserService);
    private readonly translateService = inject(TranslateService);
    private readonly snackbar = inject(SnackbarService);
    private readonly dialog = inject(MatDialog);
    private readonly destroyRef = inject(DestroyRef);

    public readonly businessService = input<BusinessServices>(BusinessServices.OM_NAM);

    private readonly teamFilterPopover =
        viewChild<ElementRef<HTMLElement & { visible: boolean; hide(): void }>>('teamFilterPopover');

    protected readonly isLoading = signal<boolean>(true);
    protected readonly isDeleting = signal<boolean>(false);
    protected readonly isAdmin = signal<boolean>(false);
    protected readonly calendarView = signal<CalendarView>('week');
    protected readonly date = signal<Date>(new Date());
    protected readonly teams = signal<ITeam[]>([]);
    protected readonly teamSearchQuery = signal<string>('');
    protected readonly selectedTeamIds = signal<Set<string>>(new Set());
    protected readonly entries = signal<OutOfOfficeOverviewEntry[]>([]);
    protected readonly userEvents = signal<UserEvent[]>([]);
    protected readonly selectedEntry = signal<OutOfOfficeOverviewEntry | null>(null);

    protected readonly selectedUserEvent = computed<UserEvent | null>(() => {
        const entry = this.selectedEntry();
        if (!entry) {
            return null;
        }
        return this.userEvents().find((e) => e.id === entry.id) ?? null;
    });

    protected readonly hasSelectedTeams = computed<boolean>(() => this.selectedTeamIds().size > 0);

    protected readonly filteredTeams = computed<ITeam[]>(() => {
        const query = this.teamSearchQuery().trim().toLowerCase();
        const teams = this.teams();

        return query ? teams.filter((team) => team.name.toLowerCase().includes(query)) : teams;
    });

    /** Country headers only earn their vertical space once the teams actually span several countries. */
    protected readonly teamGroups = computed<TeamGroup[]>(() => {
        const teams = this.filteredTeams();
        if (teams.length === 0) {
            return [];
        }

        const byCountry = new Map<Country | undefined, ITeam[]>();
        for (const team of teams) {
            const group = byCountry.get(team.country);
            if (group) {
                group.push(team);
            } else {
                byCountry.set(team.country, [team]);
            }
        }

        if (byCountry.size < 2) {
            return [{ country: undefined, teams }];
        }

        return Array.from(byCountry, ([country, groupedTeams]) => ({ country, teams: groupedTeams })).sort((a, b) =>
            this.countryLabel(a.country).localeCompare(this.countryLabel(b.country))
        );
    });

    protected readonly dateDisplay = computed<string>(() => {
        const locale = this.translateService.currentLang || this.translateService.getDefaultLang() || 'en';
        const d = this.date();

        if (this.calendarView() === 'month') {
            return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(d);
        }

        const weekStart = startOfWeek(d, { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 4);
        const formatter = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long', year: 'numeric' });
        return `${formatter.format(weekStart)} - ${formatter.format(weekEnd)}`;
    });

    public ngOnInit(): void {
        this.registerTeamFilterPopoverDismissal();
        this.loadTeams();
    }

    private registerTeamFilterPopoverDismissal(): void {
        // The team filter popover is sticky so clicks on its content don't dismiss it, which also
        // disables the popover's built-in outside-click and escape handling.
        const trigger = (): Element | null =>
            this.elementRef.nativeElement.querySelector('#team-filter-trigger') as Element | null;

        const closeOnOutsideClick = (event: MouseEvent): void => {
            const popover = this.teamFilterPopover()?.nativeElement;
            if (!popover?.visible) {
                return;
            }

            const path = event.composedPath();
            const triggerEl = trigger();
            if (path.includes(popover) || (triggerEl && path.includes(triggerEl))) {
                return;
            }

            popover.hide();
        };

        const closeOnEscape = (event: KeyboardEvent): void => {
            const popover = this.teamFilterPopover()?.nativeElement;
            if (event.key === 'Escape' && popover?.visible) {
                popover.hide();
            }
        };

        document.addEventListener('click', closeOnOutsideClick, { capture: true });
        document.addEventListener('keydown', closeOnEscape);
        this.destroyRef.onDestroy(() => {
            document.removeEventListener('click', closeOnOutsideClick, { capture: true });
            document.removeEventListener('keydown', closeOnEscape);
        });
    }

    protected isTeamSelected(team: ITeam): boolean {
        return this.selectedTeamIds().has(team.id);
    }

    protected onTeamSearchInput(event: Event): void {
        this.teamSearchQuery.set((event.target as HTMLInputElement | null)?.value ?? '');
    }

    private countryLabel(country: Country | undefined): string {
        return country ? this.translateService.instant(`countries.${country.toLowerCase()}`) : '';
    }

    protected onTeamSelectionChange(team: ITeam): void {
        this.selectedTeamIds.update((ids) => {
            const next = new Set(ids);
            if (next.has(team.id)) {
                next.delete(team.id);
            } else {
                next.add(team.id);
            }
            return next;
        });
        this.loadDelegations();
    }

    protected onToggleAllTeams(): void {
        this.selectedTeamIds.set(this.hasSelectedTeams() ? new Set() : new Set(this.teams().map((team) => team.id)));
        this.loadDelegations();
    }

    protected onNextDate(): void {
        this.selectedEntry.set(null);
        if (this.calendarView() === 'month') {
            this.date.update((d) => startOfMonth(addMonths(d, 1)));
        } else {
            this.date.update((d) => addWeeks(d, 1));
        }
        this.loadDelegations();
    }

    protected onPreviousDate(): void {
        this.selectedEntry.set(null);
        if (this.calendarView() === 'month') {
            this.date.update((d) => startOfMonth(subMonths(d, 1)));
        } else {
            this.date.update((d) => subWeeks(d, 1));
        }
        this.loadDelegations();
    }

    protected setCalendarView(view: CalendarView): void {
        this.selectedEntry.set(null);
        this.calendarView.set(view);

        if (view === 'month') {
            this.date.update((d) => startOfMonth(d));
        } else {
            this.date.update((d) => {
                const firstDayOfMonth = startOfMonth(d);
                const firstWeekStart = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
                const hasWeekendInFirstTwoDays =
                    isWeekend(addDays(firstDayOfMonth, 0)) || isWeekend(addDays(firstDayOfMonth, 1));

                return hasWeekendInFirstTwoDays
                    ? addDays(startOfWeek(firstDayOfMonth, { weekStartsOn: 1 }), 7)
                    : firstWeekStart;
            });
        }
        this.loadDelegations();
    }

    protected onSelectDelegation(entry: OutOfOfficeOverviewEntry): void {
        this.selectedEntry.set(entry);
    }

    protected onCancelSelection(): void {
        this.selectedEntry.set(null);
    }

    protected onDeleteDelegation(): void {
        const entry = this.selectedEntry();
        if (!entry) {
            return;
        }

        this.dialog
            .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
                autoFocus: false,
                data: {
                    title: 'common.out-of-office.overview.delete-confirm.title',
                    message: 'common.out-of-office.overview.delete-confirm.message',
                    confirmLabel: 'common.out-of-office.overview.delete',
                    params: { user: entry.userName }
                }
            })
            .afterClosed()
            .pipe(
                filter(Boolean),
                tap(() => this.deleteEntry(entry))
            )
            .subscribe();
    }

    private deleteEntry(entry: OutOfOfficeOverviewEntry): void {
        const previousEntries = this.entries();
        const previousUserEvents = this.userEvents();

        this.entries.update((entries) => entries.filter((e) => e.id !== entry.id));
        this.userEvents.update((events) => events.filter((e) => e.id !== entry.id));
        this.selectedEntry.set(null);
        this.isDeleting.set(true);

        this.outOfOfficeOverviewService
            .deleteDelegation(entry.id)
            .pipe(
                tap(() => this.snackbar.success('common.out-of-office.overview.delete-success')),
                catchError(() => {
                    this.entries.set(previousEntries);
                    this.userEvents.set(previousUserEvents);
                    this.selectedEntry.set(entry);
                    this.snackbar.error('common.out-of-office.overview.delete-error');
                    return EMPTY;
                }),
                finalize(() => this.isDeleting.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private loadTeams(): void {
        forkJoin({
            user: this.userService.getMe().pipe(catchError(() => of(undefined))),
            teams: this.teamService.getTeams(0, 200, this.businessService())
        })
            .pipe(
                tap(({ user, teams }: { user: IUser | undefined; teams: Paginated<ITeam> }) => {
                    this.isAdmin.set(roleNamesForStream(user, this.businessService()).includes(Roles.ADMIN));

                    const visible = this.visibleTeams(user, teams.items);
                    this.teams.set(visible);
                    this.selectedTeamIds.set(new Set(visible.map((t) => t.id)));
                    this.loadDelegations();
                }),
                catchError((error) => {
                    console.error('Failed to load teams', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    /**
     * Which teams the calendar covers depends on how the user relates to the stream: an
     * administrator oversees every team of it, the country-scoped administrative roles - zone
     * e-commerce steward, key country user - the teams of the countries they administer, and
     * everybody else only the teams they belong to. The first two hold no team assignment of
     * their own, so falling back to it would leave them with an empty calendar.
     */
    private visibleTeams(user: IUser | undefined, teams: ITeam[]): ITeam[] {
        const stream = this.businessService();

        if (!administersStream(user, stream)) {
            const assignedIds = new Set(((user?.teams ?? []) as ITeam[]).map((team) => team.id));

            return teams.filter((team) => assignedIds.has(team.id));
        }

        const countries = administeredCountries(user, stream);

        return countries.length ? teams.filter((team) => !!team.country && countries.includes(team.country)) : teams;
    }

    private loadDelegations(): void {
        const teamIds = Array.from(this.selectedTeamIds());
        const hasNoAssignedTeams = this.isAdmin() && this.teams().length === 0;
        if (teamIds.length === 0 && !hasNoAssignedTeams) {
            this.entries.set([]);
            this.userEvents.set([]);
            return;
        }

        const { startDate, endDate } = this.getDateRange();
        this.isLoading.set(true);

        const delegations$ = hasNoAssignedTeams
            ? this.outOfOfficeOverviewService.getDelegations({
                  startDate: startDate.toISOString(),
                  endDate: endDate.toISOString(),
                  businessServices: this.businessService()
              })
            : this.outOfOfficeOverviewService.getDelegations({
                  startDate: startDate.toISOString(),
                  endDate: endDate.toISOString(),
                  teamIds: teamIds.join(',')
              });

        delegations$
            .pipe(
                switchMap((response: Paginated<OutOfOfficeOverviewDelegation>) =>
                    response.items.length === 0
                        ? of([] as OutOfOfficeOverviewEntry[])
                        : forkJoin(response.items.map((delegation) => this.resolveEntry(delegation)))
                ),
                tap((resolvedEntries) => {
                    this.entries.set(resolvedEntries);
                    this.userEvents.set(resolvedEntries.map((e) => this.toUserEvent(e)));
                    this.selectedEntry.set(null);
                }),
                catchError((error) => {
                    console.error('Failed to load delegations', error);
                    return EMPTY;
                }),
                finalize(() => this.isLoading.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private getDateRange(): { startDate: Date; endDate: Date } {
        const d = this.date();
        if (this.calendarView() === 'month') {
            return { startDate: startOfMonth(d), endDate: endOfMonth(d) };
        }
        const weekStart = startOfWeek(d, { weekStartsOn: 1 });
        return { startDate: weekStart, endDate: endOfDay(addDays(weekStart, 4)) }; // Start of Monday to End of Friday
    }

    private resolveEntry(delegation: OutOfOfficeOverviewDelegation): Observable<OutOfOfficeOverviewEntry> {
        const startDate = new Date(delegation.startDate);
        const endDate = new Date(delegation.endDate);

        return forkJoin({
            owner: this.userService.getByGID(delegation.ownerGid).pipe(
                map((u) => `${u.firstName} ${u.lastName}`.trim()),
                catchError(() => of(delegation.ownerGid))
            ),
            coverage: this.userService.getByGID(delegation.coverageGid).pipe(
                map((u) => `${u.firstName} ${u.lastName}`.trim()),
                catchError(() => of(delegation.coverageGid))
            )
        }).pipe(
            map(({ owner, coverage }) => ({
                id: delegation.id,
                userName: owner,
                startDate,
                endDate,
                coverageName: coverage,
                daysOff: differenceInDays(endOfDay(endDate), startOfDay(startDate)) + 1,
                dateRange: this.formatDateRange(startDate, endDate)
            }))
        );
    }

    private toUserEvent(entry: OutOfOfficeOverviewEntry): UserEvent {
        return {
            id: entry.id,
            userName: entry.userName,
            startDate: entry.startDate,
            endDate: entry.endDate,
            showOn: []
        };
    }

    private formatDateRange(startDate: Date, endDate: Date): string {
        const locale = this.translateService.currentLang || this.translateService.getDefaultLang() || 'en';
        const formatter = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' });
        return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
    }
}
